import { z } from 'zod';
import type { ActorType } from '../constants';

/**
 * Action groups shown in the audit log filter. Audit actions are dotted `<area>.<verb>` names; a group is a
 * set of action prefixes (a prefix ending in '.' matches the whole area, otherwise it matches that exact
 * action and its sub-actions, e.g. 'lead.merge').
 */
export const AUDIT_ACTION_GROUPS = {
  auth: { label: 'Sign-in & users', prefixes: ['auth.', 'user.'] },
  lead: { label: 'Leads', prefixes: ['lead.', 'contact.', 'opportunity.', 'task.', 'note.'] },
  email: { label: 'Email', prefixes: ['email.', 'template.', 'campaign.', 'suppression.'] },
  settings: { label: 'Settings', prefixes: ['settings.'] },
  import: { label: 'Imports & discovery', prefixes: ['import.', 'discovery.'] },
  merge: { label: 'Merges & duplicates', prefixes: ['lead.merge', 'merge.', 'duplicate.'] },
  automation: { label: 'Automation', prefixes: ['automation.', 'sequence.'] },
  ai: { label: 'AI assistant', prefixes: ['ai.'] },
} as const satisfies Record<string, { label: string; prefixes: readonly string[] }>;
export type AuditActionGroup = keyof typeof AUDIT_ACTION_GROUPS;
export const AUDIT_ACTION_GROUP_KEYS = Object.keys(AUDIT_ACTION_GROUPS) as AuditActionGroup[];

/** Entity types written by the audit trail (free text in the DB; these are the known ones) → label. */
export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  business: 'Lead',
  contact: 'Contact',
  opportunity: 'Opportunity',
  task: 'Task',
  note: 'Note',
  email: 'Email',
  template: 'Template',
  campaign: 'Campaign',
  sequence: 'Sequence',
  automation_run: 'Automation run',
  suppression: 'Suppression',
  import: 'Import',
  discovery_search: 'Discovery search',
  duplicate_candidate: 'Duplicate',
  saved_view: 'Saved view',
  setting: 'Setting',
  pipeline_stage: 'Pipeline stage',
  relationship_status: 'Relationship status',
  lead_source: 'Lead source',
  product_category: 'Product category',
  product: 'Product',
  tag: 'Tag',
  custom_field: 'Custom field',
  territory: 'Territory',
  scoring_rule: 'Scoring rule',
  user: 'User',
};

const isoDateTime = z.iso.datetime({ offset: true });

/** GET /api/audit — newest first, offset pagination. */
export const AuditListQuery = z
  .object({
    entityType: z.string().trim().min(1).max(60).optional(),
    entityId: z.string().trim().min(1).max(200).optional(),
    actorUserId: z.uuid().optional(),
    /** Only entries written by this actor type (user / automation / system / import / integration). */
    actorType: z.enum(['user', 'system', 'automation', 'import', 'integration']).optional(),
    /** Action prefix, e.g. 'lead.' or 'settings.scoring'. */
    action: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .regex(/^[a-z0-9_.]+$/i, 'Use letters, digits, dots and underscores')
      .optional(),
    group: z.enum(AUDIT_ACTION_GROUP_KEYS as [AuditActionGroup, ...AuditActionGroup[]]).optional(),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    /** Free text over summary and reason. */
    q: z.string().trim().min(1).max(200).optional(),
    page: z.coerce.number().int().min(1).max(500).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((v) => !v.from || !v.to || v.from <= v.to, { message: '"From" must be before "to"', path: ['to'] });
export type AuditListQuery = z.infer<typeof AuditListQuery>;

export type AuditChange = { from: unknown; to: unknown };

export type AuditEntryDTO = {
  id: number;
  createdAt: string;
  actorType: ActorType;
  actor: { id: string; name: string } | null;
  action: string;
  entityType: string;
  entityId: string | null;
  /** The lead this entry concerns (the business itself, or the lead owning a contact/opportunity/task/note). */
  lead: { id: string; name: string; deleted: boolean } | null;
  summary: string | null;
  /** Field diffs ({ field: { from, to } }) or other structured details the action recorded. */
  changes: Record<string, unknown> | null;
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
};

/**
 * `total` is exact up to AUDIT_TOTAL_CAP; beyond that `totalCapped` is true and `total` equals the cap
 * (counting millions of rows for a page indicator is not worth the query).
 */
export type AuditListResponse = {
  items: AuditEntryDTO[];
  total: number;
  totalCapped: boolean;
  page: number;
  pageSize: number;
};
export const AUDIT_TOTAL_CAP = 10_000;

/** True when a `changes` value looks like a field-level diff ({ from, to }). */
export function isAuditChange(value: unknown): value is AuditChange {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'from' in value && 'to' in value;
}
